import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { showcaseApi } from '../api/showcaseApi';
import type { CryptoHashResponse, CryptoAesResponse, ApiError } from '../types/showcase';

type CryptoTab = 'hash' | 'aes';

const SPIN_CSS = `@keyframes spin { to { transform: rotate(360deg); } }`;

export function CryptoPanel() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<CryptoTab>('hash');

  return (
    <section aria-labelledby="crypto-panel-title">
      <style>{SPIN_CSS}</style>
      <h2
        id="crypto-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.crypto.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-4)' }}>
        {t('showcase.crypto.subtitle')}
      </p>

      {/* Tabs */}
      <div
        role="tablist"
        aria-label={t('showcase.crypto.tabsLabel')}
        style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--color-surface-border)', marginBottom: 'var(--spacing-4)' }}
      >
        {([
          { key: 'hash', label: t('showcase.crypto.tab.hash') },
          { key: 'aes',  label: t('showcase.crypto.tab.aes') },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            role="tab"
            type="button"
            aria-selected={tab === key}
            aria-controls={`crypto-tab-${key}`}
            onClick={() => setTab(key)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === key ? '2px solid var(--color-brand-primary)' : '2px solid transparent',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: tab === key ? 700 : 400,
              color: tab === key ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
              marginBottom: -2,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div id={`crypto-tab-${tab}`} role="tabpanel">
        {tab === 'hash' && <HashTab />}
        {tab === 'aes'  && <AesTab />}
      </div>

      {/* Learning */}
      <div
        style={{
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          marginTop: 'var(--spacing-4)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.crypto.learning')}
      </div>
    </section>
  );
}

// ─── HashTab ──────────────────────────────────────────────────────────────────

function HashTab() {
  const { t } = useTranslation();
  const [plain, setPlain] = useState('');
  const [hashed, setHashed] = useState('');
  const [op, setOp] = useState<'hash' | 'verify'>('hash');

  const { mutate, isPending, data, isError, error, reset } = useMutation<
    CryptoHashResponse,
    ApiError,
    { plain: string; hashed?: string; op: 'hash' | 'verify' }
  >({
    mutationFn: (req) => showcaseApi.cryptoHash(req),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    reset();
    mutate({ plain, hashed: op === 'verify' ? hashed : undefined, op });
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Op selector */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
        {(['hash', 'verify'] as const).map((o) => (
          <label
            key={o}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${op === o ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: op === o ? 'var(--color-sidebar-active-bg)' : 'transparent',
              color: op === o ? 'var(--color-brand-primary)' : 'var(--color-text-primary)',
            }}
          >
            <input type="radio" value={o} checked={op === o} onChange={() => { setOp(o); reset(); }} style={{ accentColor: 'var(--color-brand-primary)' }} />
            {o === 'hash' ? t('showcase.crypto.hash.opHash') : t('showcase.crypto.hash.opVerify')}
          </label>
        ))}
      </div>

      {/* Plain input */}
      <div style={{ marginBottom: 'var(--spacing-3)' }}>
        <label
          htmlFor="hash-plain"
          style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
        >
          {t('showcase.crypto.hash.plain')}
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginLeft: 'var(--spacing-2)', fontWeight: 400 }}>
            {t('showcase.crypto.hash.plainHint')}
          </span>
        </label>
        <input
          id="hash-plain"
          type="text"
          value={plain}
          onChange={(e) => setPlain(e.target.value)}
          placeholder="MySecret@123"
          maxLength={72}
          disabled={isPending}
          style={inputStyle}
        />
      </div>

      {/* Hashed input (verify mode) */}
      {op === 'verify' && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <label
            htmlFor="hash-hashed"
            style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
          >
            {t('showcase.crypto.hash.hashed')}
          </label>
          <input
            id="hash-hashed"
            type="text"
            value={hashed}
            onChange={(e) => setHashed(e.target.value)}
            placeholder="$2a$12$..."
            disabled={isPending}
            style={inputStyle}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
        <button type="submit" disabled={isPending || !plain.trim()} aria-busy={isPending} style={primaryBtnStyle(isPending || !plain.trim())}>
          {isPending && <SpinIcon />}
          {t('showcase.crypto.execute')}
        </button>
        {data && (
          <button type="button" onClick={() => { reset(); setPlain(''); setHashed(''); }} style={secondaryBtnStyle}>
            {t('showcase.common.reset')}
          </button>
        )}
      </div>

      {data && (
        <div role="region" aria-label={t('showcase.crypto.hash.resultLabel')} style={resultCardStyle('#22c55e')}>
          {data.op === 'hash' && (
            <>
              <dl style={dlGrid}>
                <dt style={dtStyle}>{t('showcase.crypto.hash.bcrypt')}</dt>
                <dd style={{ margin: 0 }}><code style={{ ...codeStyle, wordBreak: 'break-all' }}>{data.hashed}</code></dd>
                {data.sha256 && (
                  <>
                    <dt style={dtStyle}>SHA-256</dt>
                    <dd style={{ margin: 0 }}><code style={{ ...codeStyle, wordBreak: 'break-all' }}>{data.sha256}</code></dd>
                  </>
                )}
              </dl>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-2)', marginBottom: 0 }}>
                {t('showcase.crypto.hash.copyHint')}
              </p>
            </>
          )}
          {data.op === 'verify' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <span style={{ fontSize: 24, color: data.matches ? 'var(--color-success)' : 'var(--color-danger)' }} aria-hidden="true">
                {data.matches ? '✓' : '✗'}
              </span>
              <strong style={{ color: data.matches ? 'var(--color-success)' : 'var(--color-danger)', fontSize: 'var(--font-size-base)' }}>
                {data.matches ? t('showcase.crypto.hash.matches') : t('showcase.crypto.hash.notMatches')}
              </strong>
            </div>
          )}
        </div>
      )}

      {isError && error && <ErrorCard error={error} />}
    </form>
  );
}

// ─── AesTab ───────────────────────────────────────────────────────────────────

function AesTab() {
  const { t } = useTranslation();
  const [op, setOp] = useState<'encrypt' | 'decrypt'>('encrypt');
  const [plain, setPlain] = useState('');
  const [ciphertext, setCiphertext] = useState('');

  const { mutate, isPending, data, isError, error, reset } = useMutation<
    CryptoAesResponse,
    ApiError,
    { plain?: string; ciphertext?: string; op: 'encrypt' | 'decrypt' }
  >({
    mutationFn: (req) => showcaseApi.cryptoAes(req),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    reset();
    mutate({ plain: op === 'encrypt' ? plain : undefined, ciphertext: op === 'decrypt' ? ciphertext : undefined, op });
  }

  /**
   * #418: encrypt 결과를 decrypt 입력에 자동 채우기.
   * 암호화 결과(token)를 복사해서 decrypt 탭에 붙여넣는 번거로움 제거 → round-trip 직접 시연.
   */
  function handleUseAsDecryptInput() {
    if (data?.op === 'encrypt' && data.result) {
      setCiphertext(data.result);
      setOp('decrypt');
      reset();
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Op selector */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
        {(['encrypt', 'decrypt'] as const).map((o) => (
          <label
            key={o}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: `1px solid ${op === o ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: op === o ? 'var(--color-sidebar-active-bg)' : 'transparent',
              color: op === o ? 'var(--color-brand-primary)' : 'var(--color-text-primary)',
            }}
          >
            <input type="radio" value={o} checked={op === o} onChange={() => { setOp(o); reset(); }} style={{ accentColor: 'var(--color-brand-primary)' }} />
            {o === 'encrypt' ? t('showcase.crypto.aes.opEncrypt') : t('showcase.crypto.aes.opDecrypt')}
          </label>
        ))}
      </div>

      {op === 'encrypt' && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <label
            htmlFor="aes-plain"
            style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
          >
            {t('showcase.crypto.aes.plain')}
          </label>
          <textarea
            id="aes-plain"
            value={plain}
            onChange={(e) => setPlain(e.target.value)}
            placeholder="암호화할 평문을 입력하세요"
            rows={3}
            maxLength={4096}
            disabled={isPending}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font-family-mono)' }}
          />
        </div>
      )}

      {op === 'decrypt' && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <label
            htmlFor="aes-cipher"
            style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
          >
            {t('showcase.crypto.aes.ciphertext')}
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginLeft: 'var(--spacing-2)', fontWeight: 400 }}>
              {t('showcase.crypto.aes.ciphertextHint')}
            </span>
          </label>
          <textarea
            id="aes-cipher"
            value={ciphertext}
            onChange={(e) => setCiphertext(e.target.value)}
            placeholder="encrypt 결과를 붙여넣으세요"
            rows={3}
            maxLength={8192}
            disabled={isPending}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font-family-mono)' }}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
        <button
          type="submit"
          disabled={isPending || (op === 'encrypt' ? !plain.trim() : !ciphertext.trim())}
          aria-busy={isPending}
          style={primaryBtnStyle(isPending || (op === 'encrypt' ? !plain.trim() : !ciphertext.trim()))}
        >
          {isPending && <SpinIcon />}
          {t('showcase.crypto.execute')}
        </button>
        {data && (
          <button type="button" onClick={() => { reset(); setPlain(''); setCiphertext(''); }} style={secondaryBtnStyle}>
            {t('showcase.common.reset')}
          </button>
        )}
      </div>

      {data && (
        <div role="region" aria-label={t('showcase.crypto.aes.resultLabel')} style={resultCardStyle('#22c55e')}>
          <dl style={dlGrid}>
            <dt style={dtStyle}>{t('showcase.crypto.aes.op')}</dt>
            <dd style={{ margin: 0 }}><code style={codeStyle}>{data.op}</code></dd>
            <dt style={dtStyle}>{t('showcase.crypto.aes.result')}</dt>
            <dd style={{ margin: 0 }}>
              <code
                style={{ ...codeStyle, wordBreak: 'break-all', display: 'block', background: 'var(--color-surface-elevated)', padding: 'var(--spacing-2)', borderRadius: 'var(--radius-sm)' }}
              >
                {data.result}
              </code>
            </dd>
          </dl>
          {/* #418: encrypt 결과를 바로 decrypt 입력에 채워 round-trip 시연 */}
          {data.op === 'encrypt' && (
            <div style={{ marginTop: 'var(--spacing-3)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)' }}>
              <button
                type="button"
                onClick={handleUseAsDecryptInput}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  background: 'var(--color-brand-primary)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('showcase.crypto.aes.useAsDecryptInput')}
              </button>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', margin: 0 }}>
                {t('showcase.crypto.aes.encryptHint')}
              </p>
            </div>
          )}
        </div>
      )}

      {isError && error && <ErrorCard error={error} />}
    </form>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function ErrorCard({ error }: { error: ApiError }) {
  return (
    <div role="alert" style={errorCardStyle}>
      <strong style={{ color: 'var(--color-danger)' }}>{error.code}</strong>
      <p style={{ margin: 'var(--spacing-1) 0 0', color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)' }}>{error.message}</p>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-mono)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
};

function primaryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: 'var(--spacing-3) var(--spacing-6)',
    background: disabled ? 'var(--color-text-muted)' : 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'var(--font-family-sans)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
  };
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'transparent',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

function resultCardStyle(borderColor: string): React.CSSProperties {
  return {
    border: `1px solid ${borderColor}`,
    borderRadius: 'var(--radius-md)',
    padding: 'var(--spacing-4)',
    background: 'var(--color-surface-muted)',
    marginBottom: 'var(--spacing-4)',
  };
}

const errorCardStyle: React.CSSProperties = {
  border: '1px solid var(--color-danger)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-4)',
  background: 'var(--color-surface-muted)',
  marginBottom: 'var(--spacing-4)',
};

const dtStyle: React.CSSProperties = {
  color: 'var(--color-text-muted)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const codeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const dlGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: 'var(--spacing-2) var(--spacing-4)',
  fontSize: 'var(--font-size-sm)',
};

function SpinIcon() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: '2px solid rgba(255,255,255,0.3)',
        borderTopColor: 'var(--color-text-inverse)',
        borderRadius: '50%',
        display: 'inline-block',
        animation: 'spin 0.8s linear infinite',
      }}
    />
  );
}
